/**
 * free-shipping-bar.js
 * Live-Aktualisierung des aufgewerteten Gratisversand-Fortschrittsbalkens
 * (Snippet free-shipping-bar.liquid).
 *
 * Hört auf das Theme-Event 'cart:update' (Hinzufügen, Mengenänderung, Entfernen).
 * Liest cart.total_price aus event.detail.resource (Fallback /cart.js).
 *
 * Wichtig:
 * - Es wird NUR der innere [data-fsb-msg]-Span aktualisiert, nie das ganze
 *   Element/innerHTML → Feier-Elemente (Check/Sparkle) bleiben erhalten.
 * - Celebration nur beim Übergang „nicht erreicht" → „erreicht".
 * - Pulse nur bei echter Wertänderung.
 * - aria-valuenow nutzt den ECHTEN Prozentwert, nicht die visuelle Mindestfüllung.
 */
if (!customElements.get('free-shipping-bar')) {
  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      this.threshold = parseInt(this.dataset.thresholdCents, 10) || 5000;
      this._prevTier = this.dataset.fsbTier || null;
      this._prevReached = this.classList.contains('free-shipping-bar--reached');
      this._prevTotal = null;
      this._pulseTimer = null;
      this._celebrateTimer = null;
      this._onCartUpdate = this._onCartUpdate.bind(this);
      document.addEventListener('cart:update', this._onCartUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:update', this._onCartUpdate);
      clearTimeout(this._pulseTimer);
      clearTimeout(this._celebrateTimer);
    }

    _onCartUpdate(event) {
      const cart = event && event.detail && event.detail.resource;
      if (cart && typeof cart.total_price === 'number') {
        this.render(cart.total_price);
      } else {
        this._refreshFromServer();
      }
    }

    _refreshFromServer() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          if (cart && typeof cart.total_price === 'number') this.render(cart.total_price);
        })
        .catch(() => {});
    }

    render(totalCents) {
      const reached = totalCents >= this.threshold;
      const remaining = Math.max(this.threshold - totalCents, 0);
      let percent = this.threshold > 0 ? (totalCents / this.threshold) * 100 : 0;
      if (percent > 100) percent = 100;
      const displayPercent = Math.max(percent, 6); // Endowed Progress (nur visuell)

      const tier = reached ? 'd' : percent >= 80 ? 'c' : percent >= 40 ? 'b' : 'a';
      const minRemaining = Math.max(2000 - totalCents, 0); // bis zum Mindestbestellwert (20 €)
      const changed = this._prevTotal !== null && this._prevTotal !== totalCents;

      // Fill + Zustände
      const fill = this.querySelector('[data-fsb-fill]');
      if (fill) fill.style.width = displayPercent + '%';
      this.classList.toggle('free-shipping-bar--reached', reached);
      this.classList.toggle('free-shipping-bar--near', !reached && percent >= 80);
      this.dataset.fsbTier = tier;

      // Text nur im inneren Span (Feier-Elemente bleiben unberührt)
      const msg = this.querySelector('[data-fsb-msg]') || this.querySelector('[data-fsb-text]');
      if (msg) msg.innerHTML = this._tierMarkup(tier, remaining, minRemaining);

      // A11y: echter Prozentwert + valuetext
      const track = this.querySelector('[role="progressbar"]');
      if (track) {
        track.setAttribute('aria-valuenow', String(Math.round(percent)));
        track.setAttribute(
          'aria-valuetext',
          reached
            ? 'Kostenloser Versand erreicht'
            : 'Noch ' + this._formatMoney(remaining) + ' bis zum kostenlosen Versand'
        );
      }

      // Meilenstein-Ansage nur bei Stufenwechsel (nicht bei jedem Cent)
      if (tier !== this._prevTier) {
        const live = this.querySelector('[data-fsb-live]');
        if (live) live.textContent = this._milestoneText(tier, remaining);
      }

      // Pulse nur bei echter Wertänderung
      if (changed && !this._prefersReducedMotion()) {
        this._triggerPulse();
      }

      // Celebration nur beim Übergang false→true
      if (reached && !this._prevReached && !this._prefersReducedMotion()) {
        this._triggerCelebrate();
      }

      this._prevReached = reached;
      this._prevTier = tier;
      this._prevTotal = totalCents;
    }

    _triggerPulse() {
      this.classList.remove('free-shipping-bar--pulse');
      // Reflow erzwingen, damit die Animation neu startet
      void this.offsetWidth;
      this.classList.add('free-shipping-bar--pulse');
      clearTimeout(this._pulseTimer);
      this._pulseTimer = setTimeout(() => this.classList.remove('free-shipping-bar--pulse'), 500);
    }

    _triggerCelebrate() {
      this.classList.remove('free-shipping-bar--celebrate-on');
      void this.offsetWidth;
      this.classList.add('free-shipping-bar--celebrate-on');
      clearTimeout(this._celebrateTimer);
      this._celebrateTimer = setTimeout(
        () => this.classList.remove('free-shipping-bar--celebrate-on'),
        1200
      );
    }

    _prefersReducedMotion() {
      return (
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
    }

    _tierMarkup(tier, remainingCents, minRemainingCents) {
      const r = '<strong>' + this._formatMoney(remainingCents) + '</strong>';
      switch (tier) {
        case 'd':
          return 'Geschafft – wir schenken dir den Versand! 🎉';
        case 'c':
          return 'Nur noch ' + r + ' – und die Lieferkosten gehen auf uns! 🎁';
        case 'b':
          return 'Nur noch ' + r + ' – und deine Lieferung geht auf uns 🚚';
        default:
          return (
            'Noch <strong>' +
            this._formatMoney(minRemainingCents || 0) +
            '</strong> bis zum Mindestbestellwert (20 €)'
          );
      }
    }

    _milestoneText(tier, remainingCents) {
      switch (tier) {
        case 'd':
          return 'Kostenloser Versand erreicht.';
        case 'c':
          return 'Fast am Ziel, noch ' + this._formatMoney(remainingCents) + '.';
        case 'b':
          return 'Über die Hälfte geschafft.';
        default:
          return '';
      }
    }

    _formatMoney(cents) {
      const currency =
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
      const locale = document.documentElement.lang || 'de-DE';
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
      } catch (error) {
        return (cents / 100).toFixed(2).replace('.', ',') + ' €';
      }
    }
  }

  customElements.define('free-shipping-bar', FreeShippingBar);
}
